import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as RKeyboardEvent, type MouseEvent as RMouseEvent, type PointerEvent as RPointerEvent } from 'react';
import { syncApi, watchlistApi } from '../api';
import { useApp } from '../context';
import { navigate } from '../router';
import { progressApi } from '../addons';
import { clearWidgetCache, type PlexRef } from './Widgets';

/**
 * Kaartmenu voor titels uit Plex, zoals in de Plex-apps:
 * ⋯ in de hoek (muis), rechtsklik, lang drukken (aanraakscherm) of de menutoets.
 */

interface Open { ref: PlexRef; title: string; x: number; y: number }

let current: Open | null = null;
const listeners = new Set<(o: Open | null) => void>();
function setOpen(o: Open | null) {
  current = o;
  listeners.forEach((l) => l(o));
}

// Kaarten die net uit een rij zijn gehaald (verwijderd uit Verder kijken, of bekeken)
const hidden = new Set<string>();
const hiddenListeners = new Set<() => void>();
function hide(ratingKey: string) {
  hidden.add(ratingKey);
  hiddenListeners.forEach((l) => l());
}

/** Is deze kaart weggehaald? Rijen filteren daarop zonder te herladen. */
export function useHiddenCards(): (ratingKey: string | undefined) => boolean {
  const [, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((n) => n + 1);
    hiddenListeners.add(l);
    return () => {
      hiddenListeners.delete(l);
    };
  }, []);
  return (rk) => !!rk && hidden.has(rk);
}

/** Na een wijziging: rijen (Verder kijken e.d.) stil opnieuw laden. */
function refreshRows() {
  clearWidgetCache();
  window.setTimeout(() => window.dispatchEvent(new CustomEvent('tumedia:library-changed', { detail: { sections: [] } })), 600);
}

/** Props voor een kaart: rechtsklik, lang drukken en de menutoets openen het menu. */
export function useCardMenu(ref: PlexRef | undefined, title: string) {
  const press = useRef<{ t?: number; fired: boolean; x: number; y: number }>({ fired: false, x: 0, y: 0 });
  if (!ref) return { props: {}, dots: null };

  const openAt = (x: number, y: number) => setOpen({ ref, title, x, y });
  const props = {
    onContextMenu: (e: RMouseEvent) => {
      e.preventDefault();
      openAt(e.clientX, e.clientY);
    },
    onPointerDown: (e: RPointerEvent) => {
      if (e.pointerType !== 'touch') return;
      press.current = { fired: false, x: e.clientX, y: e.clientY };
      window.clearTimeout(press.current.t);
      press.current.t = window.setTimeout(() => {
        press.current.fired = true;
        navigator.vibrate?.(10);
        openAt(press.current.x, press.current.y);
      }, 500);
    },
    onPointerMove: (e: RPointerEvent) => {
      if (Math.abs(e.clientX - press.current.x) + Math.abs(e.clientY - press.current.y) > 12) window.clearTimeout(press.current.t);
    },
    onPointerUp: () => window.clearTimeout(press.current.t),
    onPointerCancel: () => window.clearTimeout(press.current.t),
    onClickCapture: (e: RMouseEvent) => {
      // Lang gedrukt: niet ook nog de kaart openen
      if (press.current.fired) {
        e.preventDefault();
        e.stopPropagation();
        press.current.fired = false;
      }
    },
    onKeyDown: (e: RKeyboardEvent) => {
      if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
        e.preventDefault();
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        openAt(r.left + r.width / 2, r.top + r.height / 2);
      }
    },
  };
  const dots = (
    <span
      className="card-dots"
      role="button"
      tabIndex={-1}
      aria-label={`Opties voor ${title}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        openAt(r.right, r.bottom);
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="2.2" /><circle cx="12" cy="12" r="2.2" /><circle cx="12" cy="19" r="2.2" /></svg>
    </span>
  );
  return { props, dots };
}

const fmtLeft = (ms: number) => {
  const m = Math.max(1, Math.round(ms / 60000));
  return m >= 60 ? `${Math.floor(m / 60)} u, ${m % 60} m resterend` : `${m} m resterend`;
};

/** Het menu zelf; één keer in de app (Main). */
export function CardMenuHost() {
  const { plex } = useApp();
  const [o, setO] = useState<Open | null>(current);
  const [onList, setOnList] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const returnFocus = useRef<Element | null>(null);

  useEffect(() => {
    listeners.add(setO);
    return () => {
      listeners.delete(setO);
    };
  }, []);

  useEffect(() => {
    setOnList(null);
    if (!o) return;
    returnFocus.current = document.activeElement;
    if (o.ref.guid?.startsWith('plex://')) watchlistApi.state(o.ref.guid).then((r) => setOnList(r.unknown ? null : r.onList)).catch(() => setOnList(null));
  }, [o]);

  // Binnen het scherm houden
  useLayoutEffect(() => {
    if (!o || !box.current) return setPos(null);
    const r = box.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(o.x, window.innerWidth - r.width - 8));
    const top = Math.max(8, Math.min(o.y, window.innerHeight - r.height - 8));
    setPos({ left, top });
    box.current.querySelector<HTMLElement>('button')?.focus();
  }, [o]);

  useEffect(() => {
    if (!o) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) {
        if (e instanceof KeyboardEvent) e.stopPropagation();
        done();
      }
    };
    window.addEventListener('keydown', close, true);
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('scroll', done, true);
    window.addEventListener('resize', done);
    return () => {
      window.removeEventListener('keydown', close, true);
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('scroll', done, true);
      window.removeEventListener('resize', done);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o]);

  function done() {
    setOpen(null);
    (returnFocus.current as HTMLElement | null)?.focus?.();
  }

  if (!o) return null;
  const r = o.ref;
  const playable = r.type === 'movie' || r.type === 'episode';
  const inProgress = !!r.viewOffset && !!r.duration && r.viewOffset < r.duration;

  const act = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    try {
      await fn();
      after?.();
      refreshRows();
    } catch {
      // stil: Plex gaf een fout; het menu sluit gewoon
    } finally {
      setBusy(false);
      done();
    }
  };

  const go = (href: string) => {
    setOpen(null);
    navigate(href);
  };

  return (
    <div className="card-menu-layer">
      <div
        ref={box}
        className="card-menu"
        role="menu"
        aria-label={`Opties voor ${o.title}`}
        style={pos ? { left: pos.left, top: pos.top } : { left: o.x, top: o.y, visibility: 'hidden' }}
        data-focus-group
      >
        <div className="card-menu-title">{o.title}</div>
        {r.upnext ? (
          <>
            {r.href && (
              <button role="menuitem" type="button" onClick={() => go(r.href!)}>
                <span aria-hidden="true">ⓘ</span>Info bekijken
              </button>
            )}
            <button role="menuitem" type="button" disabled={busy} onClick={() => void act(() => progressApi.hideUpNext(r.upnext!.type, r.upnext!.tmdbId), () => hide(r.ratingKey))}>
              <span aria-hidden="true">✕</span>Verwijderen uit "Verder kijken"
            </button>
          </>
        ) : r.progressId ? (
          <>
            {r.href && (
              <button role="menuitem" type="button" onClick={() => go(r.href!)}>
                <span aria-hidden="true">▶</span>Verder kijken{inProgress ? ` · ${fmtLeft(r.duration! - r.viewOffset!)}` : ''}
              </button>
            )}
            <div className="card-menu-sep" role="separator" />
            <button role="menuitem" type="button" disabled={busy} onClick={() => void act(() => progressApi.remove(r.progressId!), () => hide(r.ratingKey))}>
              <span aria-hidden="true">✕</span>Verwijderen uit "Verder kijken"
            </button>
            <button role="menuitem" type="button" disabled={busy} onClick={() => void act(() => progressApi.watched(r.progressId!, true), () => hide(r.ratingKey))}>
              <span aria-hidden="true">✓</span>Markeer als bekeken
            </button>
          </>
        ) : (<>
        {playable && inProgress && (
          <button role="menuitem" type="button" onClick={() => go(`/play/${r.ratingKey}?offset=${r.viewOffset}`)}>
            <span aria-hidden="true">▶</span>Hervatten · {fmtLeft(r.duration! - r.viewOffset!)}
          </button>
        )}
        {playable && (
          <button role="menuitem" type="button" onClick={() => go(`/play/${r.ratingKey}`)}>
            <span aria-hidden="true">{inProgress ? '↺' : '▶'}</span>{inProgress ? 'Opnieuw starten' : 'Afspelen'}
          </button>
        )}
        <button role="menuitem" type="button" onClick={() => go(`/item/${r.ratingKey}`)}>
          <span aria-hidden="true">ⓘ</span>Info bekijken
        </button>
        {r.showKey && (
          <button role="menuitem" type="button" onClick={() => go(`/item/${r.showKey}`)}>
            <span aria-hidden="true">▤</span>Naar de serie
          </button>
        )}
        {onList !== null && r.guid && (
          <button role="menuitem" type="button" disabled={busy} onClick={() => void act(() => watchlistApi.set(r.guid!, !onList))}>
            <span aria-hidden="true">{onList ? '✓' : '+'}</span>{onList ? 'Van mijn lijst halen' : 'Toevoegen aan mijn lijst'}
          </button>
        )}
        <div className="card-menu-sep" role="separator" />
        {(r.onDeck || inProgress) && (
          <button role="menuitem" type="button" disabled={busy} onClick={() => void act(() => plex.removeFromContinueWatching(r.ratingKey), () => hide(r.ratingKey))}>
            <span aria-hidden="true">✕</span>Verwijderen uit "Verder kijken"
          </button>
        )}
        {(!r.watched || inProgress) && (
          <button role="menuitem" type="button" disabled={busy} onClick={() => void act(async () => { await plex.markWatched(r.ratingKey); void syncApi.watched(r.ratingKey, true); }, () => { if (r.onDeck) hide(r.ratingKey); })}>
            <span aria-hidden="true">✓</span>Markeer als bekeken
          </button>
        )}
        {(r.watched || inProgress) && (
          <button role="menuitem" type="button" disabled={busy} onClick={() => void act(async () => { await plex.markUnwatched(r.ratingKey); void syncApi.watched(r.ratingKey, false); })}>
            <span aria-hidden="true">○</span>Markeer als niet bekeken
          </button>
        )}
        </>)}
      </div>
    </div>
  );
}
